import custOfetch from '~/composables/useCustomFetch'
import type { Participant, ParticipantStatus } from '~/types/prisma'

export interface CreateParticipantPayload {
  eventId: string
  userId: string
  inviterId: string
  eventRoleId: string
  status?: ParticipantStatus
}

export interface UpdateParticipantPayload extends Partial<CreateParticipantPayload> {}

export const useParticipant = () => {
  const config = useRuntimeConfig()

  const getAll = async (
    role?: string
  ) => {
    const url = role 
      ? `${config.public.apiBase}/participant?role=${role}` 
      : `${config.public.apiBase}/participant`
    return await custOfetch(url, {
      method: 'GET',
      credentials: 'include',
    }) as Participant[]
  }

  const getPendingPayment = async () => {
    return await custOfetch(`${config.public.apiBase}/participant/pending-payment`, {
      method: 'GET',
      credentials: 'include',
    }) as any[]
  }

  const getAllByEventRole = async (
    eventRoleId: string
  ) => {
    return await custOfetch(`${config.public.apiBase}/participant/event-role/${eventRoleId}`, {
      method: 'GET',
      credentials: 'include',
    }) as Participant[]
  }

  const create = async (
    payload: CreateParticipantPayload,
  ) => {
    return await custOfetch(`${config.public.apiBase}/participant`, {
      method: 'POST',
      credentials: 'include',
      body: payload,
    }) as Participant
  }

  const update = async (
    id: string,
    payload: UpdateParticipantPayload,
  ) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}`, {
      method: 'PATCH',
      credentials: 'include',
      body: payload,
    }) as Participant
  }

  const deleteParticipant = async (
    id: string,
  ) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}`, {
      method: 'DELETE',
      credentials: 'include',
    }) as Participant
  }

  const getById = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}`, {
      method: 'GET',
      credentials: 'include',
    }) as Participant | null
  }

  const getAllWithRelations = async (
    role?: string
  ) => {
    const url = role 
      ? `${config.public.apiBase}/participant/with-relations?role=${role}` 
      : `${config.public.apiBase}/participant/with-relations`
    return await custOfetch(url, {
      method: 'GET',
      credentials: 'include',
    }) as any[]
  }

  const getMyInvites = async () => {
    return await custOfetch(`${config.public.apiBase}/participant/my`, {
      method: 'GET',
      credentials: 'include',
    }, false) as Participant[]
  }

  const getMyParticipants = async () => {
    return await custOfetch(`${config.public.apiBase}/participant/my/all`, {
      method: 'GET',
      credentials: 'include',
    }, false) as Participant[]
  }

  const acceptInvite = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}/accept`, {
      method: 'PATCH',
      credentials: 'include',
    })
  }

  const declineInvite = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}/decline`, {
      method: 'PATCH',
      credentials: 'include',
    })
  }

  const sendInvite = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}/send-invite`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const sendDonorReminder = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}/send-donor-reminder`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const sendPaymentReminder = async (id: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/${id}/send-payment-reminder`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const sendDonorReminders = async (participantIds: string[]) => {
    return await custOfetch(`${config.public.apiBase}/participant/send-donor-reminders`, {
      method: 'POST',
      credentials: 'include',
      body: { participantIds },
    }) as { sent: number; failed: string[] }
  }

  const sendTeamLeaderReminder = async (userId: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/user/${userId}/send-team-leader-reminder`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const sendTeamReminder = async (userId: string) => {
    return await custOfetch(`${config.public.apiBase}/participant/user/${userId}/send-team-reminder`, {
      method: 'POST',
      credentials: 'include',
    })
  }

  const sendTeamLeaderReminders = async (userIds: string[]) => {
    return await custOfetch(`${config.public.apiBase}/participant/send-team-leader-reminders`, {
      method: 'POST',
      credentials: 'include',
      body: { userIds },
    }) as { sent: number; failed: string[] }
  }

  const getStats = async (role?: string) => {
    let url = `${config.public.apiBase}/participant/stats`
    const params = new URLSearchParams()
    if (role) params.append('role', role)
    
    if (params.toString()) {
      url += `?${params.toString()}`
    }

    return await custOfetch(url, {
      method: 'GET',
      credentials: 'include',
    }) as { PENDING: number, INVITED: number, ACCEPTED: number, DECLINED: number, total: number }
  }

  return {
    getAll,
    getPendingPayment,
    getAllByEventRole,
    getById,
    create,
    update,
    deleteParticipant,
    getMyInvites,
    getMyParticipants,
    acceptInvite,
    declineInvite,
    sendInvite,
    sendDonorReminder,
    sendDonorReminders,
    sendPaymentReminder,
    sendTeamReminder,
    sendTeamLeaderReminder,
    sendTeamLeaderReminders,
    getStats,
    getAllWithRelations,
  }
}
